import DatePickerAvailableDaysWrapper from "components/styleguide/components/DatePickerAvailableDaysWrapper";
import moment from "moment/moment";
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useOutletContext } from "react-router-dom";
import * as yup from "yup";

import QuestionnaireStatus from "../../../../backend-models/questionnaireStatus";
import QuestionnaireType from "../../../../backend-models/questionnaireType";
import { Button, Form, Input, Select, Space, Typography } from "../../../../components/styleguide";
import useSet from "../../../../hooks/useSet";
import GroupSelect from "./GroupSelect";

const getSchema = (form, t) => {
  return yup.object().shape({
    name: yup.string().required(t("Questionnaire name is required!")),
    availableFrom: yup.date().nullable().default(undefined).required(t('"Available from" is required!')),
    availableTo: yup
      .date()
      .nullable()
      .default(undefined)
      .required(t('"Available to" is required!'))
      .when("availableFrom", (_, schema) => {
        if (form.getFieldValue("availableFrom")) {
          return schema.min(
            moment(form.getFieldValue("availableFrom")).toDate(),
            t('"Available to" should be greater than "Available from"'),
          );
        }

        return schema;
      }),
    type: yup.string().required(t("Questionnaire type is required!")),
    forumGroupIds: yup
      .array()
      .of(yup.number())
      .min(1, t("Questionnaire group is required!"))
      .required(t("Questionnaire group is required!")),
    status: yup.string().required(t("Questionnaire status is required!")),
  });
};

const QuestionnaireForm = ({
  forumId,
  initialValues,
  isLoading,
  labelCol,
  error,
  hideFields,
  showCancel,
  onFinish,
  onCancel,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const validation = Form.useYupValidator(form, () => getSchema(form, t));
  const hiddenFields = useSet(hideFields);

  useEffect(() => {
    hiddenFields.set(hideFields);
  }, [hideFields]);

  const handleAvailableFromChange = () => {
    // noinspection JSIgnoredPromiseFromCall
    form.validateFields(["availableTo"]);
  };

  const { forum } = useOutletContext();

  return (
    <Form form={form} data-testid="questionnaireForm" labelCol={labelCol} onFinish={onFinish}>
      {!hiddenFields.has("name") && (
        <Form.Item
          label={t("Questionnaire name")}
          name="name"
          rules={validation.rules}
          required={true}
          initialValue={initialValues.name}
        >
          <Input />
        </Form.Item>
      )}
      {!hiddenFields.has("availableFrom") && (
        <Form.Item
          label={t("Available from")}
          name="availableFrom"
          rules={validation.rules}
          required={true}
          initialValue={initialValues.availableFrom}
        >
          <DatePickerAvailableDaysWrapper
            removeMargins
            onChange={handleAvailableFromChange}
            eventStartDate={moment(forum?.startDate)}
            eventEndDate={moment(forum?.endDate)}
            availableDatesGetterType={"TwelveMonthsBeforeEndOfEvent"}
            defaultPickerValue={
              initialValues.availableFrom ? moment(initialValues.availableFrom) : moment(forum?.startDate)
            }
          />
        </Form.Item>
      )}
      {!hiddenFields.has("availableTo") && (
        <Form.Item
          label={t("Available to")}
          name="availableTo"
          rules={validation.rules}
          required={true}
          initialValue={initialValues.availableTo}
        >
          <DatePickerAvailableDaysWrapper
            removeMargins
            eventStartDate={moment(forum?.startDate)}
            eventEndDate={moment(forum?.endDate)}
            availableDatesGetterType={"TwelveMonthsBeforeEndOfEvent"}
            defaultPickerValue={
              initialValues.availableTo ? moment(initialValues.availableTo) : moment(forum?.startDate)
            }
          />
        </Form.Item>
      )}
      {!hiddenFields.has("type") && (
        <Form.Item
          label={t("Questionnaire type")}
          name="type"
          rules={validation.rules}
          required={true}
          initialValue={initialValues.type}
        >
          <Select>
            {QuestionnaireType.LIST.map(({ value, label }) => (
              <Select.Option key={value} value={value}>
                {t(label)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {!hiddenFields.has("forumGroupIds") && (
        <Form.Item
          label={t("Questionnaire group")}
          name="forumGroupIds"
          rules={validation.rules}
          required={true}
          initialValue={initialValues?.forumGroups?.map(({ id }) => id)}
        >
          <GroupSelect forumId={forumId} mode="multiple" />
        </Form.Item>
      )}
      {!hiddenFields.has("status") && (
        <Form.Item
          label={t("Questionnaire status")}
          name="status"
          rules={validation.rules}
          required={true}
          initialValue={initialValues.status}
        >
          <Select>
            {QuestionnaireStatus.LIST.map(({ value, label }) => (
              <Select.Option key={value} value={value}>
                {t(label)}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
      )}
      {error && <Typography.Text type="danger">{error}</Typography.Text>}
      <Space>
        <Button htmlType="submit" type="primary" disabled={isLoading} loading={isLoading}>
          {t("Save")}
        </Button>
        {showCancel && (
          <Button type="secondary" onClick={onCancel}>
            {t("Cancel")}
          </Button>
        )}
      </Space>
    </Form>
  );
};

QuestionnaireForm.propTypes = {
  forumId: PropTypes.number.isRequired,
  initialValues: PropTypes.shape({
    name: PropTypes.string,
    availableFrom: PropTypes.string,
    availableTo: PropTypes.string,
    type: PropTypes.oneOf(QuestionnaireType.LIST),
    status: PropTypes.oneOf(QuestionnaireStatus.LIST),
    forumGroups: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
      }),
    ),
  }),
  hideFields: PropTypes.arrayOf(
    PropTypes.oneOf(["name", "availableFrom", "availableTo", "type", "forumGroupIds", "status"]),
  ),
  labelCol: PropTypes.object,
  error: PropTypes.string,
  isLoading: PropTypes.bool,
  showCancel: PropTypes.bool,
  onFinish: PropTypes.func,
  onCancel: PropTypes.func,
};

QuestionnaireForm.defaultProps = {
  initialValues: {},
  hideFields: [],
  labelCol: undefined,
  error: undefined,
  isLoading: false,
  showCancel: false,
  onFinish: () => undefined,
  onCancel: () => undefined,
};

export default QuestionnaireForm;
